// =========================================================================================@@
// Last Updated Date: Mar 3, 2023
// Last Updated By: Steven Yuen
// Status Level: 3
// ===========================================================================================

import React, { useContext, useState } from 'react'
import { View } from 'oio-react'
import { Link, useRouteMatch } from 'react-router-dom'
import uiConstants from 'config/constants/ui'
import { adminManageContentTypesUrl, initiativeUrl } from 'config/constants/urls'
import { useInitiativeList, useOrganization } from 'src/core/graphql/hooks'
import EventListBlock from 'src/sites/kits/Events/components/ListBlock'
import ListItemRowSmall from 'src/sites/kits/Initiative/components/ListItemRowSmall'
import InitiativeMoreButton from 'src/sites/kits/Initiative/components/MoreButton'
import ObjectCreateControl from 'src/sites/kits/Object/components/CreateControl'
import ObjectStatusBanner from 'src/sites/kits/Object/components/StatusBanner'
import PeopleAsideBlock from 'src/sites/kits/People/components/AsideBlock'
import { EmptyContentBlock, LayoutAside, LayoutContainer, LayoutContainerInner,
   ViewModeSwitchControl, Title } from 'src/sites/kits/UI'
import { InitiativeHierarchyContext } from 'src/sites/kits/Utils/InitiativeHierarchy'

const gridNumColumns = '1[a] 2[b] 3[c-f]'

const EventHome = () => {
   const match = useRouteMatch()
   const [viewMode, setViewMode] = useState(uiConstants.viewModes.GRID)

   const { initiative: page } = useContext(InitiativeHierarchyContext)
   const { organization } = useOrganization()
   const eventInitiativeType = organization.initiativeTypes
      .find(t => t.id === page.dynamicPageType.id)

   const groupTypeIds = organization.initiativeTypes
      .filter(t => t.class === 'group')
      .map(t => t.id)

   // Get groups that have events
   const { initiativeList: groupList } = useInitiativeList({
      archived: 'exclude',
      drafts: 'include',
      hasChildrenOfTypeId: eventInitiativeType?.id,
      typeIds: groupTypeIds,
      levels: 'all',
      limit: 25
   }, {
      skip: !eventInitiativeType
   })

   return (
      <LayoutContainer>
         <LayoutContainerInner>
            <ObjectStatusBanner />
            <LayoutAside.Container>
               <LayoutAside.Content>
                  <View
                     display="flex"
                     justifyContent="space-between"
                     padding="40px 0px 0px 0px[a-c] 40px 0px 0px 0px[d-f]"
                     width="100%">
                     <Title size="lg">
                        {page.name}
                     </Title>
                     <View display="flex" alignItems="center">
                        <View display="none[a] flex[b-f]" alignItems="center">
                           <ViewModeSwitchControl
                              buttonSize="var(--baseComponentHeight-sm)"
                              currentViewMode={viewMode}
                              onListButtonClick={() => setViewMode(uiConstants.viewModes.ROW)}
                              onGridButtonClick={() => setViewMode(uiConstants.viewModes.GRID)}
                           />
                        </View>
                        {organization.currentUserCanEdit && (
                           <View display="block" width="8px" height="24px" />
                        )}
                        <ObjectCreateControl
                           buttonHeight="var(--baseComponentHeight-sm)"
                           initiativeTypes={[eventInitiativeType]}
                           parentInitiativeId={page.id}
                        />
                        {organization.currentUserCanEdit && (
                           <View display="block" width="8px" height="24px" />
                        )}
                        <InitiativeMoreButton
                           buttonSize="var(--baseComponentHeight-sm)"
                           editUrl={`${match.url}/-/edit`}
                           popoverAnchorOriginVertical="top"
                           showInitiativeName
                        />
                     </View>
                  </View>
                  {!eventInitiativeType.enabled && (
                     <EmptyContentBlock
                        height="90px"
                        message={(
                           <View>
                              {`${eventInitiativeType.namePlural} have been disabled by the Admins of ${organization.name}.`}
                              <br />
                              <Link to={adminManageContentTypesUrl} style={{ color: 'inherit' }}>
                                 <u>Click here</u>
                              </Link>
                              &nbsp;to re-enable them.
                           </View>
                        )}
                     />
                  )}
                  {eventInitiativeType.enabled && (
                     <EventListBlock
                        initiativeTypeId={eventInitiativeType.id}
                        titleSize="lg"
                        viewMode={viewMode}
                        viewModeGridNumColumns={gridNumColumns}
                     />
                  )}
               </LayoutAside.Content>
               <LayoutAside.Aside>
                  <View>
                     {groupList.items.length > 0 && (
                        <View marginBottom="30px">
                           <Title size="xs">
                              Groups with Events
                           </Title>
                           <View padding="8px 0px 24px 0px">
                              {'The following Groups in this Community have event pages of their own:'}
                           </View>
                           {groupList.items.map(initiative => (
                              <ListItemRowSmall
                                 key={initiative.id}
                                 coverUrl={initiative.coverMedia?.file.thumbnailUrlW480}
                                 dateLastUpdated={initiative.dateLastUpdated}
                                 initiativeTypeIconName="group"
                                 linkTo={`${initiativeUrl(initiative)}/-/events`}
                                 name={initiative.name}
                                 privacy={initiative.privacy}
                                 showStats={false}
                                 typeSlug={initiative.type.slug}
                                 hideThumbnail
                              />
                           ))}
                        </View>
                     )}
                     <PeopleAsideBlock />
                  </View>
               </LayoutAside.Aside>
            </LayoutAside.Container>
         </LayoutContainerInner>
      </LayoutContainer>
   )
}

export default EventHome
